import React, { useEffect } from "react";

import { useMutation } from "@apollo/client";
import { Button, Grid, Card, CardContent, CircularProgress, styled } from "@mui/material";
import { Formik, Form } from "formik";
import { useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import * as Yup from "yup";

// UI
// Graphql and Services
import { SIGNIN } from "api/mutations";
import { GraphQLError, UserSignIn } from "api/types";
import { Input } from "components";
import StorageServices from "services/Storage";
import { snackbarState } from "state/atoms";

type FormValues = {
  username: string;
  password: string;
};

const loginValidationSchema = Yup.object().shape({
  username: Yup.string().required("Required"),
  password: Yup.string().required("Required"),
});

const StyledGridContainer = styled(Grid)(() => ({
  height: "100vh",
}));

const StyledCardContent = styled(CardContent)({
  display: "flex",
  flexDirection: "column",
});

const StyledButtonWrapper = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(2),
  position: "relative",
}));

const StyledButtonLoading = styled(CircularProgress)({
  position: "absolute",
  top: "50%",
  left: "50%",
  marginTop: -12,
  marginLeft: -12,
});

const HomePage: React.FC = () => {
  const setSnack = useSetRecoilState(snackbarState);
  const navigate = useNavigate();

  const [signin] = useMutation<{
    signIn: UserSignIn;
  }>(SIGNIN);

  useEffect(() => {
    const userToken = StorageServices.getUserToken();
    if (userToken) {
      navigate("/");
    }
  }, [navigate]);

  const onSubmit = async (values: FormValues) => {
    try {
      const { data } = await signin({
        variables: values,
      });
      if (data?.signIn?.token) {
        StorageServices.addUserToken(data.signIn.token);
        navigate("/");
        setSnack({
          message: "Success",
          open: true,
          type: "success",
        });
      }
    } catch (err) {
      const error = err as GraphQLError;
      let message = "Ops! Something went wrong..";
      if (error?.graphQLErrors && error?.graphQLErrors?.length > 0) {
        message = error.graphQLErrors[0].message;
      }
      setSnack({
        message,
        open: true,
        type: "error",
      });
    }
  };
  return (
    <StyledGridContainer container alignItems="center" justifyContent="center">
      <Grid item xs={10} sm={4}>
        <Formik
          initialValues={{ username: "", password: "" }}
          onSubmit={onSubmit}
          validationSchema={loginValidationSchema}
        >
          {({ isSubmitting, isValid }) => {
            return (
              <Form autoComplete="off">
                <Card>
                  <StyledCardContent>
                    <Input label="Username" name="username" fullWidth />
                    <Input label="Password" name="password" type="password" fullWidth margin="normal" />
                    <StyledButtonWrapper>
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={!isValid || isSubmitting}
                        type="submit"
                        fullWidth
                      >
                        Enter
                      </Button>
                      {isSubmitting && <StyledButtonLoading size={24} />}
                    </StyledButtonWrapper>
                  </StyledCardContent>
                </Card>
              </Form>
            );
          }}
        </Formik>
      </Grid>
    </StyledGridContainer>
  );
};

export default HomePage;
